import './create-bespoke-rating.scss'

import { notification } from 'antd'
import { Formik, FormikHelpers } from 'formik'
import { DatePicker, Form, FormItem, InputNumber, Select, SubmitButton } from 'formik-antd'
import React from 'react'
import { Column } from 'react-display-flex'
import useSWR from 'swr'

import { fetcher } from '../../../../api'
import { InputCurrency } from '../../../../components'
import { filterProps } from '../../../../components/forms/utils'
import { config } from '../../../../config'
import { httpClient } from '../../../../util/http-client'
import { messages } from '../messages'
import { createBespokeRatingInitialValues, CreateBespokeRatingValues } from './models'
import { issuerComplianceRuleValidationSchema } from './validators'

interface CreateBespokeRatingProps {
  domain: string
  onSuccess?: () => void
}

export const CreateBespokeRating = ({ domain, onSuccess }: CreateBespokeRatingProps) => {
  const { data: issuers } = useSWR('/banking-groups', fetcher)

  const onSubmit = async (
    formData: CreateBespokeRatingValues,
    { setSubmitting }: FormikHelpers<CreateBespokeRatingValues>,
  ) => {
    try {
      const url = `/banking-groups/${formData.bankingGroupId}/compliance-rules`
      await httpClient(url, { method: 'POST', domain, data: formData })
      notification.success({ message: messages.common.success, description: messages.create.success })
      onSuccess && onSuccess()
    } catch (error) {
      notification.error({ message: messages.common.error, description: error.error_message || messages.create.error })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={createBespokeRatingInitialValues}
      validationSchema={issuerComplianceRuleValidationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      <Form name="create-bespoke-rating">
        <FormItem name="bankingGroupId" label="Banking Group" required>
          <Select
            name="bankingGroupId"
            placeholder="Select the issuer"
            showSearch
            loading={!issuers?.length}
            options={issuers
              ?.map(({ id, code, name }) => ({
                label: `${code} - ${name} - ${id} `,
                value: id,
              }))
              .sort()}
            {...filterProps}
          />
        </FormItem>
        <FormItem name="startDate" label="Start Date" required>
          <DatePicker
            name="startDate"
            aria-label="startDate"
            placeholder="Start Date"
            format={config.defaultDateFormat}
          />
        </FormItem>
        <FormItem name="maxLimitShort" label="Max Limit Short" required>
          <InputNumber name="maxLimitShort" min={0} />
        </FormItem>
        <FormItem name="maxLimitLong" label="Max Limit Long" required>
          <InputNumber name="maxLimitLong" min={0} />
        </FormItem>
        <FormItem name="maxMoneyLimitShort" label="Max Money Limit Short" required>
          <InputCurrency name="maxMoneyLimitShort" min={0} />
        </FormItem>
        <FormItem name="maxMoneyLimitLong" label="Max Money Limit Long" required>
          <InputCurrency name="maxMoneyLimitLong" min={0} />
        </FormItem>
        <Column>
          <SubmitButton htmlType="submit" type="primary">
            Save Rating
          </SubmitButton>
        </Column>
      </Form>
    </Formik>
  )
}
